import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"

function EmptyState({ loading, text }) {
  return (
    <div style={{ color: "#64748b", fontSize: 13 }}>
      {loading ? "Cargando..." : text}
    </div>
  )
}

function CollapseButton({ collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...ui.secondaryButton,
        minWidth: 42,
        minHeight: 36
      }}
    >
      {collapsed ? "+" : "−"}
    </button>
  )
}

function SummaryMetric({ label, value, helper }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

function DoctorSaturationCard({
  item,
  getSatBg,
  getSatColor,
  getSatLabel,
  formatPct
}) {
  const levelColor = getSatColor(item.level)

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 16,
        background: getSatBg(item.level),
        border: `1px solid ${levelColor}`,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        display: "grid",
        gap: 12
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        <div
          style={{
            fontWeight: 900,
            fontSize: 15,
            color: "#0f172a"
          }}
        >
          {item.doctorName}
        </div>

        <div
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            background: "#ffffff",
            border: `1px solid ${levelColor}`,
            color: levelColor,
            fontSize: 12,
            fontWeight: 900
          }}
        >
          {getSatLabel(item.level)}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 10
        }}
      >
        <div
          style={{
            ...ui.softCard,
            padding: 10,
            background: "rgba(255,255,255,0.72)",
            border: "1px solid rgba(255,255,255,0.7)"
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: 0.3,
              marginBottom: 4
            }}
          >
            Ocupación
          </div>

          <div
            style={{
              fontSize: 22,
              lineHeight: 1,
              fontWeight: 900,
              color: "#0f172a"
            }}
          >
            {formatPct(item.occupationPct)}%
          </div>
        </div>

        <div
          style={{
            ...ui.softCard,
            padding: 10,
            background: "rgba(255,255,255,0.72)",
            border: "1px solid rgba(255,255,255,0.7)"
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: 0.3,
              marginBottom: 4
            }}
          >
            Lectura
          </div>

          <div
            style={{
              fontSize: 14,
              lineHeight: 1.25,
              fontWeight: 900,
              color: levelColor
            }}
          >
            {getSatLabel(item.level)}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 14,
          background: "rgba(255,255,255,0.72)",
          border: "1px solid rgba(255,255,255,0.7)",
          color: "#334155",
          fontSize: 13,
          lineHeight: 1.45
        }}
      >
        {item.recommendation || "Sin recomendación disponible."}
      </div>
    </div>
  )
}

export default function SaturationPanel({
  saturation = [],
  collapsed,
  onToggle,
  loading,
  getSatBg,
  getSatColor,
  getSatLabel,
  formatPct
}) {
  const rows = Array.isArray(saturation) ? saturation : []

  const avgOccupation = rows.length
    ? rows.reduce((acc, item) => acc + toNumber(item?.occupationPct), 0) / rows.length
    : 0

  const highLoadCount = rows.filter((item) => isHighLoad(item?.level)).length
  const recoverableCount = rows.filter((item) => isRecoverable(item?.level)).length

  return (
    <SectionCard
      title="Saturación del día"
      subtitle="Cómo está distribuida la carga operativa"
      actions={
        <CollapseButton
          collapsed={collapsed}
          onClick={onToggle}
        />
      }
    >
      {!collapsed && (
        <div style={{ display: "grid", gap: 12 }}>
          {!!rows.length && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 10,
                alignItems: "start"
              }}
            >
              <SummaryMetric
                label="Doctores visibles"
                value={formatCount(rows.length)}
                helper="Profesionales considerados en este corte"
              />

              <SummaryMetric
                label="Ocupación promedio"
                value={`${formatPct(avgOccupation)}%`}
                helper="Promedio general del día"
              />

              <SummaryMetric
                label="Carga alta"
                value={formatCount(highLoadCount)}
                helper="Doctores en presión operativa"
              />

              <SummaryMetric
                label="Espacio recuperable"
                value={formatCount(recoverableCount)}
                helper="Doctores con margen para reactivación"
              />
            </div>
          )}

          {!rows.length ? (
            <EmptyState loading={loading} text="Sin saturación disponible." />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12,
                alignItems: "start"
              }}
            >
              {rows.map((item) => (
                <DoctorSaturationCard
                  key={item.doctorId}
                  item={item}
                  getSatBg={getSatBg}
                  getSatColor={getSatColor}
                  getSatLabel={getSatLabel}
                  formatPct={formatPct}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  )
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatCount(value) {
  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(toNumber(value))
}

function isHighLoad(level) {
  const raw = String(level || "").trim().toUpperCase()
  return raw.includes("HIGH") || raw.includes("CRITICAL") || raw.includes("ALTA")
}

function isRecoverable(level) {
  const raw = String(level || "").trim().toUpperCase()
  return (
    raw.includes("LOW") ||
    raw.includes("MEDIUM") ||
    raw.includes("BAJA") ||
    raw.includes("MEDIA")
  )
}